import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowIcon, CheckIcon } from '@/components/icons'
import { PeakWindowBar } from '@/components/programs/PeakWindowBar'
import * as programsApi from '@/api/programs'
import { trackEvent } from '@/lib/analytics'
import {
  formatProgramDate,
  participationsByAddress,
  programBannerState,
  signupUrgency,
} from '@/lib/programs'
import type { Participation, ProgramInfo } from '@/types/programs'

/**
 * The services page's driver for a program: a prominent sign-up banner while
 * sign-up is open, a short confirmation once the user takes part, nothing when
 * neither applies or the program cannot be loaded.
 */
export function ProgramBanner({ slug }: { slug: string }) {
  const [program, setProgram] = useState<ProgramInfo | null>(null)
  const [mine, setMine] = useState<Map<string, Participation> | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([programsApi.getProgram(slug, controller.signal), programsApi.listParticipations(controller.signal, { withDevices: false })])
      .then(([p, list]) => {
        setProgram(p)
        setMine(participationsByAddress(list, slug))
      })
      // A banner is a bonus: without the programs API the page simply goes without it.
      .catch(() => undefined)
    return () => controller.abort()
  }, [slug])

  const state = programBannerState(program, mine)

  useEffect(() => {
    if (state !== 'hidden') trackEvent('program_banner_view', { program: slug, state })
  }, [slug, state])

  if (!program || state === 'hidden') return null
  const route = `/programs/${program.slug}`

  if (state === 'participating') {
    const first = [...mine!.values()].sort((a, b) => a.startDate.localeCompare(b.startDate))[0]!
    return (
      <Link
        to="/programs"
        className="card group flex items-center gap-4 p-5 hover-opacity"
        onClick={() => trackEvent('program_banner_click', { program: slug, state })}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-light-green">
          <CheckIcon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-bold">Je doet mee aan {program.name}</span>
          <span className="block text-15 text-dark-blue/60">
            {first.status === 'pending'
              ? `Je start op ${formatProgramDate(first.startDate)}`
              : `Laad niet thuis tussen ${program.peakWindow.start} en ${program.peakWindow.end}, tot ${formatProgramDate(program.endsAt)}`}
          </span>
        </span>
        <span className="hidden text-15 font-semibold text-dark-purple sm:inline">Bekijk je deelname</span>
        <span className="circle-btn">
          <ArrowIcon />
        </span>
      </Link>
    )
  }

  return (
    <section className="grid overflow-hidden rounded-3xl bg-dark-blue text-white md:grid-cols-[1fr_280px] lg:grid-cols-[1fr_340px]">
      <div className="flex flex-col gap-5 p-6 md:p-10">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full bg-orange px-3 py-1 text-13 font-bold text-white">Nieuw</span>
          <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-13 font-semibold text-white/90">
            Pilot · {formatProgramDate(program.startsAt)} – {formatProgramDate(program.endsAt)}
          </span>
        </div>
        <div>
          {/* Headings are dark blue by default (index.css); this one sits on dark blue. */}
          <h2 className="max-w-[560px] text-28 font-bold leading-111 !text-white lg:text-42">
            Verdien tot €{program.signup.nextRewardEur} met {program.name}
          </h2>
          <p className="mt-3 max-w-[560px] text-base leading-normal text-white/80">
            Laad je auto deze winter niet thuis tussen {program.peakWindow.start} en {program.peakWindow.end}, als het
            stroomnet het drukst is. Je helpt het net en ontvangt na afloop een beloning.
          </p>
        </div>
        <div className="max-w-[460px]">
          <PeakWindowBar start={program.peakWindow.start} end={program.peakWindow.end} tone="dark" />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Link
            to={route}
            className="btn-cta !h-14 shrink-0 self-start whitespace-nowrap !rounded-full !px-8 !text-lg !font-bold shadow-[0_8px_24px_rgba(98,69,222,0.45)] sm:self-auto"
            onClick={() => trackEvent('program_banner_click', { program: slug, state })}
          >
            Doe mee
            <ArrowIcon />
          </Link>
          <p className="text-15 leading-snug text-white/70">
            {signupUrgency(program)}
            <br className="hidden sm:block" /> Aanmelden duurt 2 minuten.
          </p>
        </div>
      </div>
      <img
        src="/Elektrische-auto.png"
        alt=""
        aria-hidden="true"
        className="hidden size-full object-cover md:block"
      />
    </section>
  )
}
